import { ENV } from '@/common/config';
import request from '@/utils/request';
import { isInIframe } from '@/hooks/useDrag';

const STORAGE_KEY = 'searchImgByImg_cache';

// 接口返回的数据
export let searchResult = null;
// 原始图片
export let originBase64Image = '';
// 上一次识别的点位名称
export let preIdentifyPoinName = '';

const saveToCache = () => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      searchResult,
      originBase64Image,
      preIdentifyPoinName
    }));
  } catch (e) {
    console.warn('Failed to save to sessionStorage:', e);
  }
};

const loadFromCache = () => {
  if (!isInIframe) {
    try {
      const cached = sessionStorage.getItem(STORAGE_KEY);
      if (cached) {
        const data = JSON.parse(cached);
        searchResult = data.searchResult;
        originBase64Image = data.originBase64Image || '';
        preIdentifyPoinName = data.preIdentifyPoinName || '';
      }
    } catch (e) {
      console.warn('Failed to load from sessionStorage:', e);
    }
  }
};

loadFromCache();

/**
 * @description 修改上一次识别的点位名称
 */
export const changePreIdentifyPoinName = (name: string) => {
  preIdentifyPoinName = name;
  saveToCache();
};
/**
* @description 根据图片请求接口识别图片
*/
export const getSearchResult = async (file: File): Promise<{ result: any; originBase64Image: string; originFile: File; }> => {
  originBase64Image = await getBase64(file);
  let res: any = {
    data: []
  }
  try {
    const url = ENV === 'pre' ? 'https://magiccube-gateway.3vjia.com/api/turing/xservice/api/search_material/v1' : '/api/turing/xservice/api/search_material/v1'
    res = await request(url, {
      method: 'post',
      data: {
        imageBase64: originBase64Image,
        traceId: getRandomID()
      }
    });
  } catch (error) {
    console.warn('Error converting file to base64:', error);
  }

  searchResult = res?.data || [];
  saveToCache();
  return {
    result: searchResult,
    originBase64Image,
    originFile: file
  }
};
export const changeSearchResult = (result: any) => {
  searchResult = result;
  saveToCache();
}

/**
 * @description 根据坐标从 base64 图片裁剪出缩略图
 */
export const cropBase64ByBox = (base64Image: string, coordinates: [number, number, number, number]): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (ctx) {
        const [x1, y1, x2, y2] = coordinates;
        const width = x2 - x1;
        const height = y2 - y1;
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, x1, y1, width, height, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.9));
      } else {
        reject(new Error('无法获取 Canvas 上下文'));
      }
    };
    img.onerror = () => reject(new Error('图片加载失败'));
    img.src = base64Image;
  });
};

/**
 * @description 根据坐标裁剪图片
 */
export const cropImageByCoordinates = async (file: File, coordinates: number[]) => {
  const newFile = await cropFile(file, coordinates);
  return newFile;
}
/**
 * @description 从file中获取裁剪后的图片
 */
// export async function getCropFromFile(file: File): Promise<{ newFile: File | null; text: string }> {
//   const res = await getSearchResult(file);
//   const data = res.data?.[0]?.box;
//   const text = res.data?.[0]?.text;
//   preIdentifyPoinName = text;
//   if (res?.data?.length > 0 && data?.length > 0) {
//     const newFile = await cropFile(file, data);
//     return {
//       newFile,
//       text
//     };
//   }
//   return {
//     newFile: null,
//     text
//   };
// }
/**
 * @description 生成随机ID
 */
export const getRandomID = (): string => {
  return [...Array(16)].map(() => Math.random().toString(36)[2]).join('') + Date.now()
};

/**
 * @description  裁剪file文件
 */
async function cropFile(file: File, coordinates: number[]): Promise<File | null> {
  return new Promise((resolve, reject) => {
    const [x1, y1, x2, y2] = coordinates;

    // 创建一个 Image 对象
    const image = new Image();

    // 读取文件并设置Image对象的src
    const reader = new FileReader();
    reader.onload = e => {
      if (typeof e.target?.result === 'string') {
        image.src = e.target.result;

        // 当图像加载完成后执行裁剪操作
        image.onload = () => {
          // 创建一个 Canvas 元素
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          if (ctx) {
            // 设置 Canvas 大小
            canvas.width = x2 - x1;
            canvas.height = y2 - y1;

            // 绘制图像到 Canvas 上
            ctx.drawImage(image, x1, y1, x2 - x1, y2 - y1, 0, 0, x2 - x1, y2 - y1);

            // 将 Canvas 转换为 base64
            canvas.toBlob(
              blob => {
                if (blob) {
                  const croppedFile = new File([blob], file.name, {
                    type: file.type,
                    lastModified: Date.now()
                  });
                  resolve(croppedFile);
                } else {
                  console.warn('无法生成裁剪后的 Blob 对象');
                  resolve(null);
                }
              },
              file.type,
              1
            );
          } else {
            console.warn('无法获取 Canvas 上下文');
            resolve(null);
          }
        };
      } else {
        reject(new Error('无法读取文件'));
      }
    };
    reader.readAsDataURL(file);
  });
}
/**
 * @description file转base64
 */
const getBase64 = (file: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Unexpected result type'));
      }
    };
    reader.onerror = error => reject(error);
  });
};
export const base64ToBlob = (code: any) => {
  // 参数验证
  if (!code || typeof code !== 'string') {
    console.warn('Invalid base64 string: code must be a non-empty string');
    return null;
  }

  // 检查是否包含 base64 分隔符
  if (!code.includes(';base64,')) {
    console.warn('Invalid base64 string: missing ";base64," separator');
    return null;
  }

  const parts = code.split(';base64,');

  // 验证分割后的部分
  if (parts.length !== 2 || !parts[0] || !parts[1]) {
    console.warn('Invalid base64 string: incorrect format');
    return null;
  }

  const contentType = parts[0].split(':')[1] || 'image/png';
  let base64Data = parts[1];

  // 清理 base64 字符串：移除空格、换行符等无效字符
  base64Data = base64Data.replace(/\s/g, '');

  // 验证 base64 字符串格式
  if (!base64Data || base64Data.length === 0) {
    console.warn('Invalid base64 string: empty base64 data');
    return null;
  }

  try {
    // 解码base64得到二进制字符串
    const raw = window?.atob?.(base64Data);

    if (!raw) {
      console.warn('Failed to decode base64 string');
      return null;
    }

    const rawLength = raw.length;
    const uInt8Array = new Uint8Array(rawLength); // 创建8位无符号整数值的类型化数组

    for (let i = 0; i < rawLength; ++i) {
      uInt8Array[i] = raw.charCodeAt(i); // 数组接收二进制字符串
    }

    return new Blob([uInt8Array], { type: contentType });
  } catch (error) {
    // 捕获 atob 解码错误，不抛出异常，只记录警告
    console.warn('Failed to decode base64 string:', error);
    return null;
  }
}

function isBase64(str: string): boolean {
  // 检查字符串是否为有效的 Base64 编码
  const base64Pattern = /^[A-Za-z0-9+/]+={0,2}$/;
  return base64Pattern.test(str) && (str.length % 4 === 0);
}

function convertToBase64(input: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous'; // 处理跨域问题

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        const base64String = canvas.toDataURL('image/png'); // 转换为 Base64
        resolve(base64String);
      } else {
        reject(new Error('Failed to get canvas context'));
      }
    };

    img.onerror = (error) => {
      reject(error);
    };

    img.src = input; // 设置图片源
  });
}

export async function processInput(input: string): Promise<string | null> {
  if (isBase64(input)) {
    return input; // 如果是 Base64 字符串，直接返回
  } else {
    try {
      const base64String = await convertToBase64(input); // 转换为 Base64
      return base64String;
    } catch (error) {
      console.error('Error converting image to Base64:', error);
      return null;
    }
  }
}

